import {
  Eye,
  EyeOff,
  Lock,
  LockOpen,
  MousePointerClick,
  Pencil,
  Repeat,
  RotateCw,
  Trash2,
} from 'lucide-react'

import { useAppStore } from '@core/store'
import {
  type CombinacaoDeCorte,
  type Eixo,
  type PlanoDeCorte,
  angulosDe,
  aviso,
  combinacaoEmUso,
  inverter,
  normalDosAngulos,
  planoAVista,
  produtoInterno,
  ROTULO_EIXO,
} from './planosDeCorte'
import { getModelsBbox } from './sectionMath'
import InterruptorDoPreenchimento from './InterruptorDoPreenchimento'

/** Planos de corte soltos.
 *
 *  Vive dentro do painel da caixa de corte porque é a mesma ideia, mas
 *  a caixa e estes planos não se misturam: a caixa isola um pedaço,
 *  estes cortam por onde se quiser, quantos se quiserem, e tiram-se um
 *  a um.
 *
 *  Cada plano tem nome, pode esconder-se (o corte fica, o desenho sai) e
 *  travar-se (não se move nem se tira por engano). As combinações
 *  guardam um conjunto de planos ligados com nome, para voltar a ele de
 *  uma vez.
 */
export default function PlanosDeCortePanel() {
  const planos = useAppStore((s) => s.planosDeCorte)
  const porFace = useAppStore((s) => s.planoPorFace)
  const acrescentarDoEixo = useAppStore((s) => s.acrescentarPlanoDoEixo)
  const setPorFace = useAppStore((s) => s.setPlanoPorFace)
  const tirarDestravados = useAppStore((s) => s.tirarPlanosDestravados)
  const actualizar = useAppStore((s) => s.actualizarPlano)

  const centro = (): [number, number, number] => {
    const caixa = getModelsBbox()
    if (!caixa) return [0, 0, 0]
    return [
      (caixa.min.x + caixa.max.x) / 2,
      (caixa.min.y + caixa.max.y) / 2,
      (caixa.min.z + caixa.max.z) / 2,
    ]
  }

  const texto = aviso(planos)
  const travados = planos.filter((p) => p.travado).length
  const todosEscondidos = planos.length > 0 && planos.every((p) => !planoAVista(p))

  return (
    <div className="border-t border-[var(--color-border)] pt-2">
      <div className="mb-1 flex items-center gap-2">
        <span className="text-[11px] font-medium text-[var(--color-text)]">
          Planos de corte
        </span>
        {planos.length > 0 && (
          <span className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                for (const p of planos) actualizar(p.id, { mostrar: todosEscondidos })
              }}
              title={
                todosEscondidos
                  ? 'Voltar a desenhar os planos no 3D'
                  : 'Deixar de desenhar os planos no 3D (o corte mantém-se)'
              }
              className="text-[10px] text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
            >
              {todosEscondidos ? 'Mostrar todos' : 'Esconder todos'}
            </button>
            <button
              type="button"
              onClick={tirarDestravados}
              disabled={travados === planos.length}
              title={
                travados > 0
                  ? `Tirar os planos que não estão travados (${travados} travado${travados === 1 ? '' : 's'} fica${travados === 1 ? '' : 'm'})`
                  : 'Tirar todos os planos'
              }
              className="text-[10px] text-[var(--color-text-dim)] hover:text-[var(--color-text)] disabled:opacity-40"
            >
              Tirar todos
            </button>
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {/* Pela ordem dos rótulos do IFC: X, Y (Norte), Z (vertical). */}
        {(['x', 'z', 'y'] as Eixo[]).map((eixo) => (
          <button
            key={eixo}
            type="button"
            onClick={() => acrescentarDoEixo(eixo, centro())}
            title={`Plano perpendicular a ${ROTULO_EIXO[eixo]} (eixos do IFC: Z vertical, Y para Norte), pelo centro do modelo`}
            className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            + {ROTULO_EIXO[eixo]}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setPorFace(!porFace)}
          title="Clica numa face do modelo e o plano fica paralelo a ela. É a maneira de apanhar uma fachada oblíqua, que não tem ângulo redondo nenhum."
          className={`flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] ${
            porFace
              ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
              : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
          }`}
        >
          <MousePointerClick size={10} />
          {porFace ? 'Clica numa face…' : '+ Por face'}
        </button>
      </div>

      <div className="mt-2">
        <InterruptorDoPreenchimento />
      </div>

      {texto && (
        <p className="mb-1.5 rounded border border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] px-2 py-1 text-[10px] text-[var(--color-warn-text)]">
          {texto}
        </p>
      )}

      {planos.length === 0 ? (
        <p className="text-[10px] text-[var(--color-text-dim)]">
          Nenhum. Podes ter vários ao mesmo tempo e tirar cada um quando
          quiseres.
        </p>
      ) : (
        <>
          <p className="text-[10px] text-[var(--color-text-dim)]">
            No 3D, cada plano tem pegas em ciano: a do centro desloca-o ao
            longo da normal; as outras duas rodam-no à volta do centro,
            arrastando em círculo (com Shift, de 5 em 5 graus).
          </p>
          <ul className="mt-1.5 flex flex-col gap-1.5">
            {planos.map((p) => (
              <Plano key={p.id} plano={p} />
            ))}
          </ul>
          <Combinacoes />
        </>
      )}
    </div>
  )
}

const CAMPO =
  'w-14 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 text-right text-[10px] text-[var(--color-text)] disabled:opacity-50'
const ICONE =
  'rounded p-0.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)] disabled:opacity-30 disabled:hover:text-[var(--color-text-dim)]'
const NOME =
  'min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-[11px] text-[var(--color-text)] outline-none hover:border-[var(--color-border)] focus:border-[var(--color-accent-border)] focus:bg-[var(--color-bg)]'

function Plano({ plano }: { plano: PlanoDeCorte }) {
  const actualizar = useAppStore((s) => s.actualizarPlano)
  const tirar = useAppStore((s) => s.tirarPlano)
  const { azimute, elevacao } = angulosDe(plano.normal)
  const travado = !!plano.travado
  const aVista = planoAVista(plano)
  // Onde o plano está ao longo da sua própria direcção. Guardamos um
  // ponto, mas um ponto tem três números e só um deles importa: o
  // resto é deslizar dentro do próprio plano, que não muda nada.
  const posicao = produtoInterno(plano.normal, plano.ponto)

  const mover = (metros: number) => {
    if (!Number.isFinite(metros)) return
    actualizar(plano.id, {
      ponto: [
        plano.normal[0] * metros,
        plano.normal[1] * metros,
        plano.normal[2] * metros,
      ],
    })
  }

  /** Roda o plano pelos ângulos, sem o tirar do sítio: o ponto fica
   *  onde está e só a direcção muda. */
  const rodar = (az: number, el: number) => {
    if (!Number.isFinite(az) || !Number.isFinite(el)) return
    actualizar(plano.id, { normal: normalDosAngulos(az, el) })
  }

  return (
    <li
      className={`rounded border px-2 py-1.5 ${
        travado ? 'border-[var(--color-accent-border)]' : 'border-[var(--color-border)]'
      }`}
      data-campo="plano-de-corte"
    >
      <div className="flex items-center gap-1">
        <input
          type="checkbox"
          checked={plano.activo}
          onChange={(e) => actualizar(plano.id, { activo: e.target.checked })}
          className="accent-[var(--color-accent)]"
          title="Ligar ou desligar este corte sem o tirar"
        />
        <Pencil size={10} className="shrink-0 text-[var(--color-text-dim)]" aria-hidden />
        <input
          type="text"
          value={plano.nome}
          onChange={(e) => actualizar(plano.id, { nome: e.target.value })}
          aria-label="Nome do plano"
          title="Nome do plano (aparece no 3D e nas combinações)"
          placeholder="Nome do plano"
          className={NOME}
        />
        <button
          type="button"
          onClick={() => actualizar(plano.id, { mostrar: !aVista })}
          title={aVista ? 'Esconder o desenho do plano (o corte mantém-se)' : 'Mostrar o desenho do plano'}
          aria-pressed={!aVista}
          className={ICONE}
        >
          {aVista ? <Eye size={11} /> : <EyeOff size={11} />}
        </button>
        <button
          type="button"
          onClick={() => actualizar(plano.id, { travado: !travado })}
          title={
            travado
              ? 'Destravar: o plano volta a poder mover-se, rodar e ser tirado'
              : 'Travar: o plano deixa de se mover, de rodar e de poder ser tirado por engano'
          }
          aria-pressed={travado}
          className={`${ICONE} ${travado ? 'text-[var(--color-accent)]' : ''}`}
        >
          {travado ? <Lock size={11} /> : <LockOpen size={11} />}
        </button>
        <button
          type="button"
          onClick={() => actualizar(plano.id, inverter(plano))}
          disabled={travado}
          title="Trocar o lado que fica. O plano não se mexe."
          className={ICONE}
        >
          <Repeat size={11} />
        </button>
        <button
          type="button"
          onClick={() => tirar(plano.id)}
          disabled={travado}
          title={travado ? 'Travado: destrava-o para o tirar' : 'Tirar este plano'}
          className={`${ICONE} hover:text-[var(--color-error-text)]`}
        >
          <Trash2 size={11} />
        </button>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-[var(--color-text-dim)]">
        <label className="flex items-center gap-1" title="Rodar à volta da vertical, em graus">
          Az
          <input
            type="number"
            step={1}
            value={Math.round(azimute)}
            disabled={travado}
            onChange={(e) => rodar(Number(e.target.value), elevacao)}
            className={CAMPO}
          />
        </label>
        <button
          type="button"
          onClick={() => rodar(azimute + 90, elevacao)}
          disabled={travado}
          title="Rodar 90° à volta da vertical"
          className={`${ICONE} flex items-center gap-0.5`}
        >
          <RotateCw size={10} />
          90°
        </button>
        <label className="flex items-center gap-1" title="Inclinar, em graus (0 é vertical, 90 é horizontal)">
          El
          <input
            type="number"
            step={1}
            min={-90}
            max={90}
            value={Math.round(elevacao)}
            disabled={travado}
            onChange={(e) => rodar(azimute, Number(e.target.value))}
            className={CAMPO}
          />
        </label>
        <label
          className="flex items-center gap-1"
          title="Onde está o plano ao longo da sua própria direcção, em metros, a contar da origem do mundo. Aumentar corta mais."
        >
          Pos
          <input
            type="number"
            step={0.25}
            value={Number(posicao.toFixed(3))}
            disabled={travado}
            onChange={(e) => mover(Number(e.target.value))}
            className={CAMPO}
          />
        </label>
      </div>
    </li>
  )
}

/** Combinações de planos com nome: guardam os planos ligados agora, e
 *  aplicá-las liga esses e desliga os outros. */
function Combinacoes() {
  const planos = useAppStore((s) => s.planosDeCorte)
  const combinacoes = useAppStore((s) => s.combinacoesDeCorte)
  const guardar = useAppStore((s) => s.guardarCombinacao)
  const ligados = planos.filter((p) => p.activo).length

  return (
    <div className="mt-2 border-t border-[var(--color-border)] pt-2" data-campo="combinacoes-de-corte">
      <div className="mb-1 flex items-center gap-2">
        <span className="text-[11px] font-medium text-[var(--color-text)]">Combinações</span>
        <button
          type="button"
          onClick={() => guardar()}
          disabled={ligados === 0}
          title={
            ligados === 0
              ? 'Liga pelo menos um plano para o guardar numa combinação'
              : `Guardar os ${ligados} plano${ligados === 1 ? '' : 's'} ligado${ligados === 1 ? '' : 's'} agora como uma combinação com nome`
          }
          className="ml-auto rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
        >
          + Guardar os ligados
        </button>
      </div>
      {combinacoes.length === 0 ? (
        <p className="text-[10px] text-[var(--color-text-dim)]">
          Nenhuma. Liga os planos que queres juntos e guarda-os com um nome,
          para voltar a esse conjunto de uma vez.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {combinacoes.map((c) => (
            <Combinacao key={c.id} combinacao={c} planos={planos} />
          ))}
        </ul>
      )}
    </div>
  )
}

function Combinacao({
  combinacao,
  planos,
}: {
  combinacao: CombinacaoDeCorte
  planos: readonly PlanoDeCorte[]
}) {
  const aplicar = useAppStore((s) => s.aplicarCombinacaoDeCorte)
  const renomear = useAppStore((s) => s.renomearCombinacao)
  const tirar = useAppStore((s) => s.tirarCombinacao)
  const emUso = combinacaoEmUso(planos, combinacao)
  const nomes = combinacao.planos
    .map((id) => planos.find((p) => p.id === id)?.nome)
    .filter((n): n is string => !!n)

  return (
    <li
      className={`flex items-center gap-1 rounded border px-1.5 py-1 ${
        emUso ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)]' : 'border-[var(--color-border)]'
      }`}
      title={nomes.join(' + ')}
    >
      <input
        type="text"
        value={combinacao.nome}
        onChange={(e) => renomear(combinacao.id, e.target.value)}
        aria-label="Nome da combinação"
        className={NOME}
      />
      <span className="shrink-0 text-[10px] text-[var(--color-text-dim)]">
        {nomes.length} plano{nomes.length === 1 ? '' : 's'}
      </span>
      <button
        type="button"
        onClick={() => aplicar(combinacao.id)}
        disabled={emUso}
        title={`Ligar ${nomes.join(' + ')} e desligar os outros planos`}
        className="shrink-0 rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-50"
      >
        {emUso ? 'Em uso' : 'Aplicar'}
      </button>
      <button
        type="button"
        onClick={() => tirar(combinacao.id)}
        title="Apagar esta combinação (os planos ficam)"
        className={`${ICONE} hover:text-[var(--color-error-text)]`}
      >
        <Trash2 size={11} />
      </button>
    </li>
  )
}
